import React, { useMemo } from 'react';
import * as styles from './reportLayout.css';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import style from 'styles/styled-components/style';
import CoreSvg from 'src/shared/CoreSvg';
import Icon from 'src/shared/icons';
import ReportHeader from '../report-header/ReportHeader';
import { ReportPollingProvider } from './ReportPollingContext';
import usePollingReport from './usePollingReport';
import { useSearchParams } from 'react-router-dom';
import { PlatformCode } from 'src/type/common';
import {
	igReportGet,
	nbReportGet,
	ttReportGet,
	twReportGet,
	useIgReportGet,
	useNbReportGet,
	useTtReportGet,
	useTwReportGet,
	useYtReportGet,
	ytReportGet,
} from 'src/apis/orval/endpoints/v2-report/v2-report';
import {
	ProfileIgAdapter,
	ProfileNbAdapter,
	ProfileTtAdapter,
	ProfileTwAdapter,
	ProfileYtAdapter,
} from './ProfileAdapter';
import usePollingForUpdate from './usePollingForUpdate';

interface ReportLayoutProps {
	setModal: (value: boolean) => void;
	children?: React.ReactNode;
}

const ReportLayout = ({ setModal, children }: ReportLayoutProps) => {
	const [search_params] = useSearchParams();
	const platformCode = search_params.get('p') as PlatformCode;
	const platformPk = search_params.get('pk') as string;

	const reportIg = useIgReportGet(platformPk, {
		query: {
			select: (data) => new ProfileIgAdapter(data).adapt(),
			enabled: !!(platformPk && platformCode && platformCode === 'ig'),
			refetchOnWindowFocus: false,
		},
	});
	const reportYt = useYtReportGet(platformPk, {
		query: {
			select: (data) => new ProfileYtAdapter(data).adapt(),
			enabled: !!(platformPk && platformCode && platformCode === 'yt'),
			refetchOnWindowFocus: false,
		},
	});
	const reportTt = useTtReportGet(platformPk, {
		query: {
			select: (data) => new ProfileTtAdapter(data).adapt(),
			enabled: !!(platformPk && platformCode && platformCode === 'tt'),
			refetchOnWindowFocus: false,
		},
	});
	const reportNb = useNbReportGet(platformPk, {
		query: {
			select: (data) => new ProfileNbAdapter(data).adapt(),
			enabled: !!(platformPk && platformCode && platformCode === 'nb'),
			refetchOnWindowFocus: false,
		},
	});
	// const reportTw = useTwReportGet(platformPk, {
	// 	query: {
	// 		select: (data) => new ProfileTwAdapter(data).adapt(),
	// 		enabled: !!(platformPk && platformCode && platformCode === 'tw'),
	// 		refetchOnWindowFocus: false,
	// 	},
	// });

	const reportMapper = {
		ig: reportIg,
		yt: reportYt,
		nb: reportNb,
		tt: reportTt,
		// tw: reportTw,
	} as const;
	const report = reportMapper?.[platformCode];

	const reportGetMapper = useMemo(
		() => ({
			ig: igReportGet,
			yt: ytReportGet,
			nb: nbReportGet,
			tt: ttReportGet,
			tw: twReportGet,
		}),
		[],
	);

	// 리포트 대상이 바뀌면 진행 중인 polling 세션을 정리하고 새로 시작한다.
	const pollingSessionKey = `${platformCode}:${platformPk}`;

	const { isLoading: isReportLoading, isPollingFailed } = usePollingReport(
		report &&
			!report.isError &&
			!report.isLoading &&
			report.data?.isCompleted === false &&
			report.data?.isCached === false,
		report.refetch,
		pollingSessionKey,
	);
	const {
		needUpdate,
		isLoading: isUpdateLoading,
		readyForUpdate,
	} = usePollingForUpdate(
		report &&
			!report.isError &&
			!report.isLoading &&
			report.data?.isCompleted === false &&
			report.data?.isCached === true,
		() => reportGetMapper[platformCode](platformPk),
		pollingSessionKey,
	);

	return (
		<ReportPollingProvider
			value={{
				isReportLoading,
				isUpdateLoading,
				needUpdate,
				readyForUpdate,
				isPollingFailed,
			}}
		>
			<div
				className={styles.reportLayout}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => e.key === 'Escape' && setModal(false)}
			>
				<OverlayScrollbarsComponent
					className={styles.reportOverlay}
					// options={{
					// 	scrollbars: { autoHide: 'leave', autoHideDelay: 0 },
					// }}
				>
					<div className={styles.reportHeader}>
						<style.Grid flex="start" _gap="16px">
							<style.Text typo="h3">REPORT</style.Text>
							<style.Text typo="span">{}</style.Text>
						</style.Grid>

						<CoreSvg _onClick={() => setModal(false)}>
							<Icon.Close />
						</CoreSvg>
					</div>

					<ReportHeader
						isLoading={report?.isLoading}
						isError={report?.isError || isPollingFailed}
						{...report?.data?.props}
					/>

					{children}
				</OverlayScrollbarsComponent>
			</div>
		</ReportPollingProvider>
	);
};

export default ReportLayout;
